import { useState } from "react";
import "@/App.css";
import axios from "axios";
import { motion } from "framer-motion";
import { Play, ArrowRight, Mail, Loader2, Check } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMG = "/assets/bse-hero.jpg";
const TRAILER_POSTER = "/assets/owl-motto.jpg";
const POSTER_IMG = "/assets/poster.webp";
const FEST_IMG_3 = "/assets/fest2.jpg";
const FEST_IMG_4 = "/assets/fest4.webp";
const FEST_IMG_1 = "/assets/fest1.webp";


const CREDITS = [
  { role: "Directed by", name: "William Collinson & Iancu T Alin" },
  { role: "Lead Screenwriter", name: "Iancu T Alin" },
  { role: "Produced by", name: "Iancu T Alin, William Collinson" },
  { role: "Cinematography", name: "William Collinson" },
  { role: "Aerial Cinematographer", name: "JR" },
  { role: "Editing", name: "Justin Harris" },
  { role: "Music & Score", name: "Jackson Irving" },
];

const LAURELS = [
  { festival: "Show Low Film Festival", result: "Film Winner — Documentary" },
  { festival: "Cinevista Oasis Film Awards", result: "Award Winner — Season 10" },
  { festival: "Film Nest Festival", result: "Award Winner" },
  { festival: "MiraBan UK Film Awards, London", result: "Official Selection — Best Documentary" },
  { festival: "ARFF Berlin — Around International Film Festival", result: "Official Engagement" },
];

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
};

const Header = () => (
  <header data-testid="site-header" className="fixed top-0 left-0 w-full z-50 border-b border-white/5 bg-[#040406]/85 backdrop-blur-2xl">
    <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-10 py-5">
      <a href="#top" data-testid="brand-link" className="text-[0.8rem] font-semibold tracking-[0.3em] uppercase text-white">
        Behind Silent Eyes
      </a>
      <nav data-testid="site-nav" className="hidden md:flex items-center gap-10">
        <a data-testid="nav-film" href="#film" className="text-[0.7rem] tracking-[0.2em] uppercase font-medium text-[#86868b] hover:text-white transition-colors duration-300">The Film</a>
        <a data-testid="nav-laurels" href="#laurels" className="text-[0.7rem] tracking-[0.2em] uppercase font-medium text-[#86868b] hover:text-white transition-colors duration-300">Awards</a>
        <a data-testid="nav-manifesto" href="#about" className="text-[0.7rem] tracking-[0.2em] uppercase font-medium text-[#86868b] hover:text-white transition-colors duration-300">About</a>
        <a data-testid="nav-contact" href="#contact" className="text-[0.7rem] tracking-[0.2em] uppercase font-medium text-[#d4af37] border border-[#d4af37]/40 px-4 py-2 hover:bg-[#d4af37] hover:text-black transition-colors duration-300">Inquire</a>
      </nav>
      <a data-testid="nav-contact-mobile" href="#contact" className="md:hidden text-[0.7rem] tracking-[0.2em] uppercase font-medium text-[#d4af37] border border-[#d4af37]/40 px-4 py-2">Inquire</a>
    </div>
  </header>
);

const Hero = () => (
  <section id="top" data-testid="hero-section" className="relative min-h-screen flex items-center justify-center grain overflow-hidden">
    <div className="absolute inset-0">
      <img src={HERO_IMG} alt="Behind Silent Eyes Production LLC logo" className="w-full h-full object-cover opacity-60" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#040406]/80 via-[#040406]/50 to-[#040406]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,#040406_85%)]" />
    </div>
    <div className="relative z-10 max-w-4xl mx-auto text-center px-6 pt-32 pb-24">
      <motion.p {...fadeUp} className="text-[0.7rem] tracking-[0.4em] uppercase font-medium text-[#d4af37] mb-8" data-testid="hero-eyebrow">
        Independent Vision. Authentic Stories. Lasting Impact.
      </motion.p>
      <motion.h1 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tight text-white leading-[1.05] mb-6" data-testid="hero-headline">
        Behind Silent Eyes Production LLC
      </motion.h1>
      <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }} className="text-sm sm:text-base font-medium tracking-[0.5em] uppercase text-[#d4af37] mb-10" data-testid="hero-motto">
        Bringing Reality to a Vision
      </motion.p>
      <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.3 }} className="text-base sm:text-lg text-[#a8a8ad] font-light leading-relaxed max-w-2xl mx-auto mb-12" data-testid="hero-intro">
        We don't just make films. We give stories a place to live. An independent film production company dedicated to meaningful documentaries, independent films, and original cinematic projects.
      </motion.p>
      <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.4 }} className="flex flex-col sm:flex-row items-center justify-center gap-5">
        <a href="#film" data-testid="hero-watch-btn" className="inline-flex items-center gap-3 border border-white/30 text-white px-10 py-4 text-[0.75rem] font-semibold tracking-[0.25em] uppercase hover:bg-white hover:text-black transition-colors duration-500">
          <Play className="w-3.5 h-3.5" /> Watch the Story
        </a>
        <a href="#contact" data-testid="hero-inquire-btn" className="inline-flex items-center gap-3 text-[#d4af37] px-4 py-4 text-[0.75rem] font-semibold tracking-[0.25em] uppercase hover:gap-5 transition-all duration-500">
          Inquiries <ArrowRight className="w-3.5 h-3.5" />
        </a>
      </motion.div>
    </div>
    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 w-px h-16 bg-gradient-to-b from-transparent via-[#d4af37]/50 to-transparent" />
  </section>
);

const Film = () => (
  <section id="film" data-testid="film-section" className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-36">
    <motion.div {...fadeUp}>
      <span className="block text-[0.7rem] tracking-[0.35em] uppercase font-semibold text-[#d4af37] mb-3" data-testid="film-eyebrow">Featured Production</span>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight text-white mb-14" data-testid="film-heading">Behind Silent Eyes</h2>
    </motion.div>
    <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-14 lg:gap-20 bg-[#0b0b0f] border border-white/5 p-8 md:p-14 lg:p-20">
      <motion.div {...fadeUp} className="flex flex-col justify-between gap-10">
        <div className="relative aspect-video bg-[#020203] border border-white/5 overflow-hidden" data-testid="trailer-theatre">
          <img src={TRAILER_POSTER} alt="Behind Silent Eyes — Bringing Reality to a Vision owl artwork" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-transparent" />
          <div className="absolute top-0 left-0 right-0 flex items-center justify-center pt-4">
            <span className="text-[0.7rem] tracking-[0.35em] uppercase font-semibold text-[#d4af37] bg-black/50 backdrop-blur-sm px-5 py-2" data-testid="trailer-runtime">Coming to view in 2027</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-x-8 gap-y-2 text-[0.7rem] tracking-[0.2em] uppercase font-semibold text-[#d4af37]" data-testid="film-specs">
          <span>Documentary Short</span>
          <span>Runtime 13 min 16 sec</span>
          <span>True Story</span>
        </div>
      </motion.div>
      <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.15 }}>
        <h3 className="font-display text-2xl sm:text-3xl uppercase tracking-tight text-white mb-8" data-testid="film-synopsis-heading">The Story</h3>
        <p className="text-[#c3c3c9] font-light leading-[1.9] text-base mb-6 text-justify" data-testid="film-synopsis-1">
          An award-winning documentary exploring the realities of Romania's institutional orphanage system, adoption, identity, and the lifelong impact of childhood experiences. Through personal history and firsthand perspectives, the film brings attention to stories that might otherwise remain unheard.
        </p>
        <p className="text-[#c3c3c9] font-light leading-[1.9] text-base mb-10 text-justify" data-testid="film-synopsis-2">
          The production follows the true story of Ianu Tebetru — raised in a Romanian orphanage, adopted by an American family at age seven, surrendered to state social services at twelve, and moved between group homes until nineteen, before finding his path from addiction to recovery.
        </p>
        <div data-testid="film-credits-list">
          {CREDITS.map((c) => (
            <div key={c.role} className="flex flex-wrap justify-between items-baseline gap-2 py-3.5 border-b border-white/5" data-testid={`film-credit-${c.role.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <span className="font-display text-base text-white">{c.name}</span>
              <span className="text-[0.65rem] tracking-[0.2em] uppercase font-semibold text-[#d4af37]">{c.role}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  </section>
);

const Laurels = () => (
  <section id="laurels" data-testid="laurels-section" className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-36 border-t border-white/5">
    <motion.div {...fadeUp}>
      <span className="block text-[0.7rem] tracking-[0.35em] uppercase font-semibold text-[#d4af37] mb-3" data-testid="laurels-eyebrow">Festival Recognition</span>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight text-white mb-6" data-testid="laurels-heading">Awards & International Recognition</h2>
      <p className="text-[#a8a8ad] font-light leading-relaxed max-w-2xl mb-14" data-testid="laurels-intro">
        We are proud of the recognition our productions have received through international film festivals and independent cinema organizations.
      </p>
    </motion.div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="laurels-grid">
      {LAURELS.map((l, i) => (
        <motion.div key={l.festival} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }} className="bg-[#111117] border border-white/5 border-l-2 border-l-[#d4af37] p-6 md:p-7" data-testid={`laurel-card-${i}`}>
          <p className="font-display text-lg text-white mb-1.5 leading-snug">{l.festival}</p>
          <p className="text-[0.7rem] tracking-[0.15em] uppercase font-medium text-[#c9a94a]">{l.result}</p>
        </motion.div>
      ))}
    </div>
  </section>
);

const About = () => (
  <section id="about" data-testid="manifesto-section" className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-36 border-t border-white/5">
    <motion.div {...fadeUp}>
      <span className="block text-[0.7rem] tracking-[0.35em] uppercase font-semibold text-[#d4af37] mb-3" data-testid="manifesto-eyebrow">About Our Company</span>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight text-white mb-14" data-testid="manifesto-heading">Independent in Vision. Authentic in Storytelling.</h2>
    </motion.div>
    <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-14 lg:gap-24">
      <motion.div {...fadeUp}>
        <p className="text-lg sm:text-xl text-[#c3c3c9] font-light leading-[1.9] mb-8" data-testid="manifesto-p1">
          Behind Silent Eyes Production LLC is an independently owned and privately operated production company established with a vision to create meaningful, professionally developed cinematic projects.
        </p>
        <p className="text-lg sm:text-xl text-[#c3c3c9] font-light leading-[1.9] mb-8" data-testid="manifesto-p2">
          We believe filmmaking is more than entertainment. It is an opportunity to document experiences, explore important subjects, preserve personal histories, and create a lasting connection between stories and audiences.
        </p>
        <p className="text-lg sm:text-xl text-[#c3c3c9] font-light leading-[1.9]" data-testid="manifesto-p3">
          Our company operates independently, maintaining creative direction and ownership of its productions while pursuing opportunities for international recognition, distribution, and exhibition.
        </p>
      </motion.div>
      <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.15 }}>
        <div className="grid grid-cols-2 gap-4 mb-10" data-testid="crew-image">
          <div className="relative aspect-[4/5] overflow-hidden border border-white/5">
            <img src={FEST_IMG_1} alt="Show Low Film Festival red carpet award photo" className="w-full h-full object-cover" />
          </div>
          <div className="relative aspect-[4/5] overflow-hidden border border-white/5">
            <img src={FEST_IMG_3} alt="Show Low Film Festival winner holding award" className="w-full h-full object-cover" />
          </div>
          <div className="relative aspect-[4/3] col-span-2 overflow-hidden border border-white/5">
            <img src={FEST_IMG_4} alt="Show Low Film Festival winner on the red carpet with trophy" className="w-full h-full object-cover" />
          </div>
        </div>
        <h3 className="text-[0.7rem] tracking-[0.3em] uppercase font-semibold text-[#86868b] mb-2" data-testid="crew-index-heading">Production Crew Index</h3>
        <div data-testid="crew-index">
          {CREDITS.map((c, i) => (
            <div key={i} className="flex flex-wrap justify-between items-baseline gap-2 py-4 border-b border-white/5" data-testid={`crew-node-${i}`}>
              <span className="font-display text-lg text-white">{c.name}</span>
              <span className="text-[0.65rem] tracking-[0.2em] uppercase font-semibold text-[#d4af37]">{c.role}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  </section>
);

const Book = () => (
  <section data-testid="book-section" className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-36 border-t border-white/5">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24 items-center">
      <motion.div {...fadeUp}>
        <span className="block text-[0.7rem] tracking-[0.35em] uppercase font-semibold text-[#d4af37] mb-3" data-testid="book-eyebrow">The Source</span>
        <h2 className="font-display text-3xl sm:text-4xl uppercase tracking-tight text-white mb-8" data-testid="book-heading">Behind Silent Eyes: The Secrets Within</h2>
        <p className="text-[#c3c3c9] font-light leading-[1.9] text-base mb-6" data-testid="book-description">
          The documentary is adapted from the autobiographical book by Ianu Tebetru — a true account of abandonment, abuse, group homes, and the journey toward recovery and rebuilding a life as an adult.
        </p>
        <div className="flex flex-col gap-3" data-testid="book-details">
          <div className="flex justify-between items-baseline py-3 border-b border-white/5">
            <span className="text-[0.65rem] tracking-[0.2em] uppercase font-semibold text-[#d4af37]">Author</span>
            <span className="font-display text-base text-white">Ianu Tebetru</span>
          </div>
          <div className="flex justify-between items-baseline py-3 border-b border-white/5">
            <span className="text-[0.65rem] tracking-[0.2em] uppercase font-semibold text-[#d4af37]">Publisher</span>
            <span className="font-display text-base text-white">Dorrance Publishing Company</span>
          </div>
        </div>
      </motion.div>
      <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.15 }} className="relative bg-[#0b0b0f] border border-white/5 p-10 md:p-14">
        <p className="font-display text-xl sm:text-2xl text-white italic leading-[1.7] mb-8" data-testid="book-pull-quote">
          "Some stories are never forgotten. Some truths deserve to be heard."
        </p>
        <p className="text-[0.7rem] tracking-[0.25em] uppercase font-semibold text-[#d4af37]" data-testid="book-pull-quote-attrib">Behind Silent Eyes Production LLC</p>
      </motion.div>
    </div>
  </section>
);

const Upcoming = () => (
  <section data-testid="upcoming-section" className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-36 border-t border-white/5">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24">
      <motion.div {...fadeUp}>
        <span className="block text-[0.7rem] tracking-[0.35em] uppercase font-semibold text-[#d4af37] mb-3" data-testid="upcoming-eyebrow">Upcoming Projects</span>
        <h2 className="font-display text-3xl sm:text-4xl uppercase tracking-tight text-white mb-8" data-testid="upcoming-heading">The Journey Continues</h2>
        <p className="text-[#c3c3c9] font-light leading-[1.9] text-base mb-6" data-testid="upcoming-description">
          Our creative journey continues beyond a single production. Behind Silent Eyes Production LLC is developing additional projects that explore personal experiences, human resilience, identity, and stories with the potential to create meaningful connections.
        </p>
        <p className="text-[#c3c3c9] font-light leading-[1.9] text-base" data-testid="upcoming-description-2">
          We look forward to sharing more about our upcoming productions as they progress through development and production.
        </p>
      </motion.div>
      <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.15 }}>
        <span className="block text-[0.7rem] tracking-[0.35em] uppercase font-semibold text-[#d4af37] mb-3" data-testid="vision-eyebrow">Our Vision</span>
        <h2 className="font-display text-3xl sm:text-4xl uppercase tracking-tight text-white mb-8" data-testid="vision-heading">A Body of Work to Be Remembered</h2>
        <p className="text-[#c3c3c9] font-light leading-[1.9] text-base mb-6" data-testid="vision-description">
          We believe every individual has a story, every experience carries meaning, and every voice deserves the opportunity to be heard.
        </p>
        <p className="text-[#c3c3c9] font-light leading-[1.9] text-base" data-testid="vision-description-2">
          Our vision is to establish Behind Silent Eyes Production LLC as a recognized independent production company known for creative independence, authentic storytelling, and meaningful cinematic work.
        </p>
      </motion.div>
    </div>
  </section>
);

const SUBJECTS = ["Professional Inquiry", "Media Correspondence", "Distribution", "Production Collaboration", "Festival Programming", "General Inquiry"];

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: SUBJECTS[0], message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Inquiry sent — thank you. We'll be in touch.");
      setForm({ name: "", email: "", subject: SUBJECTS[0], message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send your inquiry. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  const field = "w-full bg-transparent border-0 border-b border-white/15 focus:border-[#d4af37] focus:ring-0 outline-none text-white placeholder:text-[#5a5a60] text-base font-light py-4 px-0 transition-colors duration-300";

  return (
    <section id="contact" data-testid="contact-section" className="max-w-[900px] mx-auto px-6 md:px-10 py-28 md:py-36">
      <motion.div {...fadeUp} className="text-center mb-14">
        <span className="block text-[0.7rem] tracking-[0.35em] uppercase font-semibold text-[#d4af37] mb-3" data-testid="contact-eyebrow">Contact & Professional Inquiries</span>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight text-white mb-6" data-testid="contact-heading">Start the Conversation</h2>
        <p className="text-[#a8a8ad] font-light leading-relaxed max-w-xl mx-auto" data-testid="contact-intro">
          For professional inquiries, media correspondence, distribution discussions, and production-related communications, we welcome hearing from you.
        </p>
      </motion.div>
      <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="bg-[#0b0b0f] border border-white/5 p-8 md:p-14">
        {sent ? (
          <div className="text-center py-14" data-testid="contact-success">
            <div className="w-14 h-14 rounded-full border border-[#d4af37]/50 flex items-center justify-center mx-auto mb-6">
              <Check className="w-6 h-6 text-[#d4af37]" />
            </div>
            <h3 className="font-display text-2xl text-white mb-3 uppercase tracking-tight">Received</h3>
            <p className="text-[#a8a8ad] font-light mb-8">Thank you for reaching out. We will respond as soon as possible.</p>
            <button onClick={() => setSent(false)} data-testid="contact-send-another" className="text-[0.7rem] tracking-[0.25em] uppercase font-semibold text-[#d4af37] hover:text-white transition-colors">Send another message</button>
          </div>
        ) : (
          <form onSubmit={onSubmit} data-testid="contact-form" className="flex flex-col gap-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" data-testid="contact-name-input" className={field} />
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Your email" data-testid="contact-email-input" className={field} />
            </div>
            <select value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject-select" className={`${field} appearance-none cursor-pointer bg-[#0b0b0f]`}>
              {SUBJECTS.map((s) => <option key={s} value={s} className="bg-[#0b0b0f]">{s}</option>)}
            </select>
            <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Your message" data-testid="contact-message-input" className={`${field} resize-none`} />
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6 mt-8">
              <a href="mailto:behindsilenteyesproduction@gmail.com" data-testid="contact-email-link" className="inline-flex items-center gap-2.5 text-[0.7rem] tracking-[0.2em] uppercase font-semibold text-[#86868b] hover:text-[#d4af37] transition-colors">
                <Mail className="w-4 h-4" /> behindsilenteyesproduction@gmail.com
              </a>
              <button type="submit" disabled={sending} data-testid="contact-submit-button" className="inline-flex items-center gap-3 border border-[#d4af37]/50 text-[#d4af37] px-10 py-4 text-[0.75rem] font-semibold tracking-[0.25em] uppercase hover:bg-[#d4af37] hover:text-black transition-colors duration-500 disabled:opacity-50 disabled:cursor-not-allowed">
                {sending && <Loader2 className="w-4 h-4 animate-spin" />}
                {sending ? "Sending" : "Send Inquiry"}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </section>
  );
};

const Footer = () => (
  <footer data-testid="site-footer" className="border-t border-white/5">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 flex flex-col md:flex-row items-center justify-between gap-6">
      <p className="text-[0.8rem] font-semibold tracking-[0.3em] uppercase text-white" data-testid="footer-brand">Behind Silent Eyes Production LLC</p>
      <p className="text-[0.65rem] tracking-[0.3em] uppercase text-[#d4af37] font-medium" data-testid="footer-motto">Independent in Vision. Authentic in Storytelling.</p>
      <p className="text-[0.65rem] tracking-[0.15em] uppercase text-[#86868b]" data-testid="footer-copy">© 2026 Behind Silent Eyes Production LLC. All Rights Reserved.</p>
    </div>
  </footer>
);

export default function App() {
  return (
    <div className="App bg-ink text-white min-h-screen">
      <Header />
      <main>
        <Hero />
        <Film />
        <Laurels />
        <About />
        <Book />
        <Upcoming />
        <Contact />
      </main>
      <Footer />
      <Toaster position="bottom-center" theme="dark" />
    </div>
  );
}
